// /payments — Global PayPal IPN transaction search (v1-B)
// Spec: docs/plans/admin-tooling.md §8
//
// Filters: payer_email (exact), txn_id (exact), status, min_amount ($ to cents),
//          since/until (date to unix). Apply button triggers TanStack Query.
// Results: desktop table / mobile card list (< 640 px).
// Cursor pagination via next_cursor ("Load more" pattern mirrors audit.tsx).
// USD amounts: real-world PayPal USD — displayed with "$" per task spec.
// NO em-dash (U+2014) anywhere. Uses en-dash or hyphen only.

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { Button, Input, Field, ErrorChip, PageHeader } from '@awakenedlands/ui';
import { StatusPill, paymentStatusToPill } from '@/components/admin/status-pill';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DpsRow {
  dp_id: number;
  dp_buyer: number;
  dp_for: number;
  dp_type: number;
  dp_time: number;
  dp_txn: string;
  payment_status: string;
  payer_email: string;
  payment_fee: number;
  payment_type: string;
  payer_status: string;
  residence_country: string;
  amount_cents: number;
}

interface TransactionsResponse {
  ok: boolean;
  rows: DpsRow[];
  next_cursor: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const _usdFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
function fmtUsd(cents: number): string {
  return _usdFormatter.format(cents / 100);
}

function fmtUtc(unixSec: number): string {
  return new Date(unixSec * 1000).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

function dateToUnix(dateStr: string): number | null {
  if (!dateStr) return null;
  const ts = new Date(dateStr).getTime();
  return Number.isNaN(ts) ? null : Math.floor(ts / 1000);
}

function buildQueryString(
  filters: {
    payerEmail: string;
    txnId: string;
    status: string;
    minAmountDollars: string;
    since: string;
    until: string;
  },
  cursor?: string,
): string {
  const params = new URLSearchParams();
  if (filters.payerEmail.trim()) params.set('payer_email', filters.payerEmail.trim());
  if (filters.txnId.trim()) params.set('txn_id', filters.txnId.trim());
  if (filters.status) params.set('status', filters.status);
  if (filters.minAmountDollars.trim()) {
    const cents = Math.round(parseFloat(filters.minAmountDollars) * 100);
    if (!Number.isNaN(cents)) params.set('min_amount', String(cents));
  }
  const sinceUnix = dateToUnix(filters.since);
  if (sinceUnix !== null) params.set('since', String(sinceUnix));
  const untilUnix = dateToUnix(filters.until);
  if (untilUnix !== null) params.set('until', String(untilUnix));
  if (cursor) params.set('cursor', cursor);
  params.set('limit', '50');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ── Row components ─────────────────────────────────────────────────────────────

function TxnTableRow({ row }: { row: DpsRow }) {
  return (
    <tr className="hover:bg-panel-elev" data-testid={`payment-row-${row.dp_id}`}>
      <td className="px-3 py-2 font-mono text-xs text-ink-low">
        <Link
          to="/payments/$txn_id"
          params={{ txn_id: row.dp_txn }}
          className="text-primary underline underline-offset-2"
          data-testid={`txn-link-${row.dp_id}`}
        >
          {row.dp_txn}
        </Link>
      </td>
      <td className="px-3 py-2 tabular-nums">
        <Link
          to="/users/$user_id/transactions"
          params={{ user_id: String(row.dp_buyer) }}
          className="text-primary underline underline-offset-2"
          data-testid={`buyer-link-${row.dp_id}`}
        >
          #{row.dp_buyer}
        </Link>
      </td>
      <td className="px-3 py-2 tabular-nums font-mono text-xs">
        {fmtUsd(row.amount_cents)}
      </td>
      <td className="px-3 py-2">
        <StatusPill status={paymentStatusToPill(row.payment_status)} />
      </td>
      <td className="px-3 py-2 text-xs text-ink-low font-mono break-all">{row.payer_email}</td>
      <td className="px-3 py-2 text-xs text-ink-low font-mono whitespace-nowrap tabular-nums">
        {fmtUtc(row.dp_time)}
      </td>
    </tr>
  );
}

function TxnCard({ row }: { row: DpsRow }) {
  return (
    <div
      className="rounded-sm border border-border bg-panel p-3 space-y-1.5"
      data-testid={`payment-card-${row.dp_id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <Link
          to="/payments/$txn_id"
          params={{ txn_id: row.dp_txn }}
          className="font-mono text-xs text-primary underline underline-offset-2 truncate"
        >
          {row.dp_txn}
        </Link>
        <StatusPill status={paymentStatusToPill(row.payment_status)} />
      </div>
      <div className="text-xs text-ink-low font-mono space-y-0.5">
        <div>
          <span className="text-ink-mid">Buyer </span>
          <Link
            to="/users/$user_id/transactions"
            params={{ user_id: String(row.dp_buyer) }}
            className="text-primary underline underline-offset-2"
          >
            #{row.dp_buyer}
          </Link>
          <span className="text-ink-mid"> - </span>
          <span className="tabular-nums">{fmtUsd(row.amount_cents)}</span>
        </div>
        <div className="break-all">{row.payer_email}</div>
        <div className="tabular-nums">{fmtUtc(row.dp_time)}</div>
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PaymentsPage() {
  // Draft filter state
  const [draftPayerEmail, setDraftPayerEmail] = useState('');
  const [draftTxnId, setDraftTxnId] = useState('');
  const [draftStatus, setDraftStatus] = useState('');
  const [draftMinAmount, setDraftMinAmount] = useState('');
  const [draftSince, setDraftSince] = useState('');
  const [draftUntil, setDraftUntil] = useState('');

  // Applied filters (trigger query)
  const [applied, setApplied] = useState({
    payerEmail: '',
    txnId: '',
    status: '',
    minAmountDollars: '',
    since: '',
    until: '',
  });

  // Pagination
  const [cursor, setCursor] = useState<string | null>(null);
  const [accRows, setAccRows] = useState<DpsRow[]>([]);
  const [loadMoreBusy, setLoadMoreBusy] = useState(false);
  const [rateLimitMsg, setRateLimitMsg] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<TransactionsResponse>({
    queryKey: ['admin-payments', applied],
    queryFn: async () => {
      const qs = buildQueryString(applied);
      const res = await apiFetch<TransactionsResponse>(`/admin/payments/transactions${qs}`);
      if (res.status === 429) {
        setRateLimitMsg('Rate limited. Please wait a moment and retry.');
        throw new Error('rate_limited');
      }
      setRateLimitMsg(null);
      if (!res.ok) throw new Error('Failed to load transactions.');
      setAccRows(res.data.rows);
      setCursor(res.data.next_cursor);
      return res.data;
    },
  });

  function handleApply() {
    setApplied({
      payerEmail: draftPayerEmail,
      txnId: draftTxnId,
      status: draftStatus,
      minAmountDollars: draftMinAmount,
      since: draftSince,
      until: draftUntil,
    });
  }

  async function handleLoadMore() {
    if (!cursor) return;
    setLoadMoreBusy(true);
    const qs = buildQueryString(applied, cursor);
    const res = await apiFetch<TransactionsResponse>(`/admin/payments/transactions${qs}`);
    setLoadMoreBusy(false);
    if (res.status === 429) {
      setRateLimitMsg('Rate limited. Please wait a moment and retry.');
      return;
    }
    if (!res.ok) {
      setRateLimitMsg('Failed to load more transactions. Please try again.');
      return;
    }
    setRateLimitMsg(null);
    setAccRows((prev) => [...prev, ...res.data.rows]);
    setCursor(res.data.next_cursor);
  }

  const displayedRows: DpsRow[] = accRows.length > 0 ? accRows : (data?.rows ?? []);
  const hasRows = displayedRows.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payment Transactions"
        description="Global PayPal IPN payment search. All amounts are real-world USD."
      />

      {/* Filter bar */}
      <div
        className="flex flex-wrap gap-3 items-end rounded-sm border border-border bg-muted p-4"
        data-testid="filter-bar"
      >
        <Field label="Payer Email">
          <Input
            id="filter-payer-email"
            type="email"
            placeholder="buyer@example.com"
            value={draftPayerEmail}
            onChange={(e) => setDraftPayerEmail(e.target.value)}
            className="w-48"
            data-testid="filter-payer-email"
          />
        </Field>
        <Field label="Transaction ID">
          <Input
            id="filter-txn-id"
            type="text"
            placeholder="e.g. 7PH19..."
            value={draftTxnId}
            onChange={(e) => setDraftTxnId(e.target.value)}
            className="w-40 font-mono"
            data-testid="filter-txn-id"
          />
        </Field>
        <Field label="Status">
          <select
            id="filter-status"
            className="rounded-sm border border-border bg-panel px-2 py-2 text-sm text-ink-high font-mono h-9"
            value={draftStatus}
            onChange={(e) => setDraftStatus(e.target.value)}
            data-testid="filter-status"
          >
            <option value="">Any</option>
            <option value="Completed">Completed</option>
            <option value="Reversed">Reversed</option>
            <option value="Pending">Pending</option>
            <option value="Failed">Failed</option>
          </select>
        </Field>
        <Field label="Min Amount ($)">
          <Input
            id="filter-min-amount"
            type="number"
            min="0"
            step="0.01"
            placeholder="e.g. 5.00"
            value={draftMinAmount}
            onChange={(e) => setDraftMinAmount(e.target.value)}
            className="w-28"
            data-testid="filter-min-amount"
          />
        </Field>
        <Field label="Since (date)">
          <Input
            id="filter-since"
            type="date"
            value={draftSince}
            onChange={(e) => setDraftSince(e.target.value)}
            className="w-36"
            data-testid="filter-since"
          />
        </Field>
        <Field label="Until (date)">
          <Input
            id="filter-until"
            type="date"
            value={draftUntil}
            onChange={(e) => setDraftUntil(e.target.value)}
            className="w-36"
            data-testid="filter-until"
          />
        </Field>
        <Button
          type="button"
          onClick={handleApply}
          disabled={isLoading}
          data-testid="apply-filters-btn"
        >
          Apply
        </Button>
      </div>

      {/* Rate limit warning */}
      {rateLimitMsg && (
        <ErrorChip data-testid="rate-limit-msg">{rateLimitMsg}</ErrorChip>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
        </div>
      )}

      {/* Error */}
      {isError && !rateLimitMsg && (
        <ErrorChip data-testid="payments-error">Failed to load transactions.</ErrorChip>
      )}

      {/* Empty state */}
      {!isLoading && !isError && !hasRows && (
        <p className="text-sm text-ink-low" data-testid="payments-empty">
          No transactions found.
        </p>
      )}

      {/* Results */}
      {!isLoading && !isError && hasRows && (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto rounded-sm border border-border">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2 font-medium text-ink-mid">Txn ID</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Buyer</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Amount</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Status</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Payer Email</th>
                  <th className="px-3 py-2 font-medium text-ink-mid">Date (UTC)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {displayedRows.map((row) => (
                  <TxnTableRow key={row.dp_id} row={row} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="flex flex-col gap-3 sm:hidden" data-testid="payments-cards">
            {displayedRows.map((row) => (
              <TxnCard key={row.dp_id} row={row} />
            ))}
          </div>

          {/* Load more */}
          {cursor && (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="secondary"
                onClick={() => { void handleLoadMore(); }}
                disabled={loadMoreBusy}
                data-testid="load-more-btn"
              >
                {loadMoreBusy ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-ink-low border-t-primary" />
                    Loading...
                  </span>
                ) : (
                  'Load more'
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
